import { Locations } from 'components';
import { useFormik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { routes } from 'settings/navigation/routes';

import classNames from 'classnames';
import { getFeedCriteriaSelectorV3 } from 'store/selectors/feedv3';
import styles from './styles.module.scss';
import * as yup from 'yup';
import { setCriteriaAction } from 'store/actions/feedv3';
import { useSearchInstanceV3Effect } from 'pages/Properties/ClientFeed/hooks/useSearchInstanceV3Effect';
import { defaultPropertyStatusToFetch } from 'settings/constants/properties';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { useAgentSearchInstance } from 'pages/Properties/AgentFeed/hooks/useAgentSearchInstance';
import LocationsV2 from 'components/Form/LocationsV2';
import { Button, Modal } from 'components-antd';
import { Icons } from './../Icons';
import { isCassAddressSet } from 'helpers';

const noPropertiesAgentText =
  "We weren't able to find any data for this address. Please try a different address.";
const noPropertiesClientText =
  "We weren't able to find any data for this address. Please try a different address or contact your agent for assistance.";

const initialValues = {
  Locations: [],
};

type Props = {
  formClassname?: string;
  valuesWrapperClassName?: string;
  searchWrapperClassName?: string;
  isSearchPage?: boolean;
  disabled?: boolean;
  valueClassName?: string;
  isAgent?: boolean;
  userId?: number;
};

const validationSchema = yup.object().shape({
  Locations: yup.array().min(1, 'Please enter a location'),
});

export const LocationSearch: React.FC<Props> = ({
  formClassname,
  valuesWrapperClassName,
  searchWrapperClassName,
  isSearchPage = false,
  disabled = false,
  valueClassName,
  userId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const isResults = pathname === '/properties/search/results';
  const { criteria } = useSelector(getFeedCriteriaSelectorV3);
  const { isAgent } = useSelector(getUserRolesMapSelector);
  const getAgentSearchInstance = useAgentSearchInstance();
  const getSearchInstanceV3 = useSearchInstanceV3Effect();
  const [noPropertiesText, setNoPropertiesText] = useState('');
  const [showNoPropertiesModal, setShowNoPropertiesModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigateToListingsPage = () =>
    history.push(
      routes.searchResults + history.location.search + (userId ? `?userId=${userId}` : ''),
    );

  const callbackCassAddressPropertiesCheck = (properties) => {
    // Callback function that checks if there are no
    // CASS Address properties, then show no properties modal.
    if (properties.length === 0) {
      setNoPropertiesText(isAgent ? noPropertiesAgentText : noPropertiesClientText);
      setShowNoPropertiesModal(true);
    } else navigateToListingsPage();
  };

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: initialValues,
    onSubmit: (values) => {
      const searchCriteria: any = {
        ...(!isSearchPage ? { Status: defaultPropertyStatusToFetch } : { ...criteria }),
        Locations: values.Locations,
      };
      const isCass = values?.Locations?.some(({ deliveryLine1 }) => deliveryLine1);
      if (!isCass) {
        searchCriteria.HomeType = ['Single', 'Townhouse', 'Condo'];
      }
      dispatch(
        setCriteriaAction({
          criteria: searchCriteria,
        }),
      );
      if (pathname !== routes.searchResults) {
        if (isCassAddressSet(searchCriteria?.Locations)) {
          setLoading(true);
          if (isAgent) {
            getAgentSearchInstance(
              {
                criterias: searchCriteria,
              },
              (err, resp) => {
                setLoading(false);
                if (!err) callbackCassAddressPropertiesCheck(resp?.data?.items);
              },
            );
          } else {
            getSearchInstanceV3({ criterias: searchCriteria }, (err, resp) => {
              setLoading(false);
              if (!err) callbackCassAddressPropertiesCheck(resp?.data?.items);
            });
          }
        } else navigateToListingsPage();
      } else if (isAgent) {
        getAgentSearchInstance({
          criterias: searchCriteria,
        });
      } else {
        getSearchInstanceV3({ criterias: searchCriteria });
      }
    },
  });

  useEffect(() => {
    if (criteria?.Locations && isSearchPage) {
      formik.setFieldValue('Locations', criteria?.Locations);
    }
  }, [criteria, isSearchPage]);

  return (
    <Fragment>
      <form
        onSubmit={formik.handleSubmit}
        className={classNames(styles.formWrapper, formClassname)}
      >
        <LocationsV2
          shouldFilterStreetLevelResults
          shouldFilterStateLevelResults
          className={styles.locations}
          activeInputClassName={styles.locationsInput}
          activeInputIconClassName={styles.locationsIcon}
          valuesWrapperClassName={classNames(styles.locationsValues, valuesWrapperClassName)}
          valuesContainerClassName={styles.valuesContainer}
          valueClassName={classNames(styles.locationsValue, valueClassName)}
          suffixClassName={styles.suffixIcon}
          placeholderClassName={classNames(
            styles.placeholder,
            isResults ? styles.result : styles.search,
          )}
          loaderClassName={styles.loader}
          name="Locations"
          onResult={(_, preparedData) => {
            formik.setFieldValue('Locations', preparedData);
          }}
          placeholder="Enter a city, address, neighborhood, school or ZIP"
          error={formik.touched.Locations ? (formik.errors.Locations as string) : ('' as string)}
          value={formik?.values?.Locations || []}
          testid="search_locations"
          disabled={disabled}
          showSuffix
          showOnlyCassAddresses
          multiple
          customLoading={loading}
        />
      </form>

      {/* No Properties Modal (New Search Page) */}
      <Modal
        open={showNoPropertiesModal}
        footer={null}
        width={675}
        className={styles.noPropertiesModal}
        onCancel={() => setShowNoPropertiesModal(false)}
      >
        <div className={styles.content}>
          <Icons variant={Icons.NO_PROPERTIES} />
          <p className={styles.header}>No results found.</p>
          <span className={styles.noPropertiesText}>{noPropertiesText}</span>
          <Button variant="secondary" onClick={() => setShowNoPropertiesModal(false)}>
            Continue
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
};
